import { useTranslation } from '@shared/i18n';
import { useTelegramStatus } from '@features/settings/hooks/useTelegram';
import { useWeeklyTrends } from '../hooks/useWeeklyTrends';

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0,
  }).format(value);
}

export function WeeklyTrendsCard() {
  const { t } = useTranslation();
  const { data: telegramStatus } = useTelegramStatus();
  const { data, isLoading } = useWeeklyTrends();

  if (!telegramStatus?.linked) return null;
  if (isLoading) {
    return (
      <div className="card p-6">
        <p className="text-sm text-gray-500">{t('common.loading')}</p>
      </div>
    );
  }
  if (!data?.trends?.length) return null;

  const maxRevenue = Math.max(...data.trends.map((d) => d.revenue), 1);

  return (
    <div className="card p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">{t('weekly_trends.title')}</h3>

      <div className="grid grid-cols-3 gap-4 text-center mb-4">
        <div>
          <div className="text-lg font-bold text-gray-900">
            {formatCurrency(data.summary.avgRevenue)}
          </div>
          <div className="text-xs text-gray-500">{t('weekly_trends.avg_revenue')}</div>
        </div>
        <div>
          <div className="text-lg font-bold text-gray-900">{data.summary.totalOrders}</div>
          <div className="text-xs text-gray-500">{t('weekly_trends.total_orders')}</div>
        </div>
        <div>
          <div className="text-lg font-bold text-gray-900">
            {new Date(data.summary.topDay).toLocaleDateString('ru-RU', {
              weekday: 'short',
              day: 'numeric',
              month: 'short',
            })}
          </div>
          <div className="text-xs text-gray-500">{t('weekly_trends.top_day')}</div>
        </div>
      </div>

      <div className="flex items-end gap-1 h-20">
        {data.trends.map((day) => (
          <div key={day.date} className="flex-1 flex flex-col items-center gap-1">
            <div
              className="w-full bg-indigo-400 rounded-t"
              style={{
                height: `${Math.max((day.revenue / maxRevenue) * 100, 4)}%`,
              }}
            />
            <span className="text-[10px] text-gray-400">
              {new Date(day.date).toLocaleDateString('ru-RU', { weekday: 'short' })}
            </span>
          </div>
        ))}
      </div>

      {data.trends.some((d) => d.recommendation) && (
        <div className="mt-3 p-2 bg-indigo-50 rounded text-xs text-indigo-700">
          {data.trends.find((d) => d.recommendation)?.recommendation}
        </div>
      )}
    </div>
  );
}
